import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, IndianRupee, Layers, Trash2, Plus, XCircle, RefreshCcw, Search, Filter, Edit } from 'lucide-react';
import { formatDate } from '@/lib/utils';

const woStatusColors = {
  created: 'bg-slate-100 text-slate-700 border-slate-200',
  issued: 'bg-blue-50 text-blue-700 border-blue-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  received: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed: 'bg-red-50 text-red-700 border-red-200',
  qc_failed: 'bg-red-50 text-red-700 border-red-200'
};

export function getWOStatus(wo) {
  if (wo.is_closed || wo.status === 'closed') return { status: 'closed', label: 'Closed' };
  const processes = wo.production_processes || [];
  if (processes.length === 0) return { status: 'created', label: 'Created' };
  const hasQCFailure = processes.some(p => (p.jobs || []).some(j => j.quality_passed === false));
  const allComplete = processes.every(p => (p.completed_quantity || 0) >= wo.quantity);
  if (hasQCFailure) {
    return allComplete
      ? { status: 'qc_failed', label: 'Completed (QC Failed)' }
      : { status: 'qc_failed', label: 'QC Failed' };
  }
  if (allComplete) return { status: 'completed', label: 'Completed' };
  const hasInProgress = processes.some(p => (p.jobs || []).some(j => (j.quantity_issued || 0) > (j.quantity_completed || 0)));
  if (hasInProgress) return { status: 'in_progress', label: 'In Progress' };
  const hasIssued = processes.some(p => (p.issued_quantity || 0) > 0);
  if (hasIssued) return { status: 'issued', label: 'Issued' };
  return { status: 'created', label: 'Created' };
}

export default function WORegisterView({
  workOrders,
  user,
  onSelectWO,
  onOpenCostView,
  onFetchShortageItems,
  onDeleteWO,
  onOpenCloseDialog,
  onOpenAddAdhocProcess,
  onAcknowledgeSOUpdate,
  onSyncMaterialBlocking,
  onRefresh,
  onEditWO,
  highlightedWO,
  currentPage,
  totalPages,
  totalOrders,
  onPageChange,
  onSearch,
  isLoading,
}) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const searchTimeoutRef = React.useRef(null);

  // Debounced search - call backend after 300ms of no typing
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      if (onSearch) {
        onSearch(value);
      }
    }, 300);
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Client-side status filter only (search is handled by backend)
  const filteredOrders = React.useMemo(() => {
    let filtered = workOrders;
    if (statusFilter !== 'all') {
      filtered = filtered.filter(wo => {
        const s = getWOStatus(wo).status;
        return s === statusFilter;
      });
    }
    return filtered;
  }, [workOrders, statusFilter]);

  const hasJobCardsIssued = (wo) => wo.production_processes?.some(p => p.jobs && p.jobs.length > 0);

  const canCloseWO = (wo) => wo.status !== 'closed' && wo.status !== 'received' && !wo.is_closed;

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search WO, item, SO..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9 h-9"
              data-testid="wo-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-9" data-testid="wo-status-filter">
              <Filter className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="created">Created</SelectItem>
              <SelectItem value="issued">Issued</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={onRefresh} data-testid="refresh-wo-btn">
            <RefreshCcw className="w-4 h-4 mr-1.5" />
            Refresh
          </Button>
          {(user?.allowed_modules?.includes('work_orders') || user?.is_owner) && (
            <Button variant="outline" size="sm" onClick={onSyncMaterialBlocking} title="Recalculate blocked quantities for all WOs with status 'Created'">
              Sync Blocking
            </Button>
          )}
        </div>
      </div>

      {/* Top Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1">
          <Button variant="outline" size="sm" onClick={() => onPageChange(1)} disabled={currentPage === 1 || isLoading}>First</Button>
          <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading}>Prev</Button>
          <span className="px-3 text-sm text-muted-foreground">Page {currentPage}/{totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading}>Next</Button>
          <Button variant="outline" size="sm" onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages || isLoading}>Last</Button>
        </div>
      )}

      {/* Table */}
      <Card className="border overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="w-[180px]">WO Number</TableHead>
                <TableHead>Item</TableHead>
                <TableHead className="text-center w-[90px]">Qty</TableHead>
                <TableHead className="w-[180px]">Progress</TableHead>
                <TableHead className="w-[110px]">SO Ref</TableHead>
                <TableHead className="w-[100px]">Delivery</TableHead>
                <TableHead className="w-[110px]">Status</TableHead>
                <TableHead className="text-right w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.map((wo) => {
                const woStatus = getWOStatus(wo);
                const processes = wo.production_processes || [];
                const completedQty = processes.length > 0
                  ? Math.min(...processes.map(p => p.completed_quantity || 0))
                  : 0;
                const progressPct = wo.quantity > 0 ? Math.min(100, Math.round((completedQty / wo.quantity) * 100)) : 0;

                return (
                  <TableRow
                    key={wo.id}
                    id={`wo-${wo.id}`}
                    className={`cursor-pointer transition-colors hover:bg-accent/50 ${highlightedWO === wo.id ? 'ring-2 ring-primary bg-accent' : ''}`}
                    onClick={() => onSelectWO(wo.id)}
                    data-testid={`wo-row-${wo.work_order_number}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-sm">{wo.work_order_number}</span>
                        {wo.wo_type === 'child' && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-purple-50 text-purple-700 border-purple-200">Child</Badge>
                        )}
                        {wo.wo_type === 'internal' && !wo.parent_wo_id && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-slate-50 text-slate-600 border-slate-200">Internal</Badge>
                        )}
                        {wo.child_work_orders?.length > 0 && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-purple-50 text-purple-600 border-purple-200">{wo.child_work_orders.length} child</Badge>
                        )}
                        {wo.so_updated && !wo.so_update_acknowledged_by?.includes(user?.id) && (
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1 py-0 bg-orange-50 text-orange-700 border-orange-300 animate-pulse cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); onAcknowledgeSOUpdate(wo.id); }}
                            title="Click to acknowledge"
                          >
                            SO Updated
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="min-w-0">
                        <p className="text-sm truncate font-medium">{wo.item_name}</p>
                        {wo.specs && (
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {typeof wo.specs === 'string' ? wo.specs : Object.values(wo.specs).join(', ')}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-semibold text-sm">{wo.quantity}</span>
                      <span className="text-xs text-muted-foreground ml-0.5">{wo.unit}</span>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Progress value={progressPct} className="h-2 flex-1" />
                          <span className="text-xs font-medium text-muted-foreground w-8 text-right">{progressPct}%</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{completedQty}/{wo.quantity} completed</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {wo.is_sfg_wo ? (
                        // Bug 4: SFG rows previously showed '-' here (wo.so_number is null on
                        // SFG children). Surface the parent count from wo.wo_allocations (the
                        // canonical clubbed-allocation array) so shop floor sees scope at a
                        // glance; full chain (SOs, customers, delivery dates) is on the detail
                        // page via the /parent-chain endpoint.
                        (() => {
                          const n = (wo.wo_allocations?.length || wo.parent_work_orders?.length || 0);
                          return (
                            <span className="inline-flex items-center gap-1.5 text-sm">
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">SFG</Badge>
                              <span className="text-muted-foreground">{n} parent{n === 1 ? '' : 's'}</span>
                            </span>
                          );
                        })()
                      ) : (
                        <span className="text-sm text-muted-foreground">{wo.so_number || '-'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{formatDate(wo.delivery_date)}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs ${woStatusColors[woStatus.status]}`}>
                        {woStatus.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-0.5">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          onClick={() => onOpenCostView(wo)} title="Production Cost" data-testid={`view-cost-${wo.work_order_number}`}>
                          <IndianRupee className="w-3.5 h-3.5" />
                        </Button>
                        {/* Edit WO - only for created/issued status */}
                        {(wo.status === 'created' || wo.status === 'issued') && !wo.is_closed && onEditWO && (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => onEditWO(wo)} title="Edit WO" data-testid={`edit-wo-${wo.work_order_number}`}>
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {(wo.status === 'created' || wo.status === 'issued') && !wo.is_closed && (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                            onClick={() => onFetchShortageItems(wo.id, wo.work_order_number)} title="Create Child WOs" data-testid={`create-child-wo-${wo.work_order_number}`}>
                            <Layers className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {!wo.is_closed && wo.status !== 'received' && wo.status !== 'completed' && wo.status !== 'closed' && (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                            onClick={() => onOpenAddAdhocProcess(wo)} title="Add Ad-hoc Process">
                            <Plus className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {!hasJobCardsIssued(wo) && wo.status === 'created' && (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => onDeleteWO(wo)} title="Delete WO">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {canCloseWO(wo) && (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => onOpenCloseDialog(wo)} title="Close WO">
                            <XCircle className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {filteredOrders.length === 0 && (
            <div className="text-center py-16">
              <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
              <p className="text-muted-foreground">
                {workOrders.length === 0 ? 'No work orders yet' : 'No work orders match your filter'}
              </p>
              {workOrders.length === 0 && (
                <p className="text-sm text-muted-foreground mt-1">Create work orders from the Sales Order page</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination + count */}
      <div className="flex flex-col items-center gap-2 pb-16">
        <p className="text-xs text-muted-foreground">
          {isLoading ? 'Loading...' : `Showing ${filteredOrders.length} of ${totalOrders} orders`}
        </p>
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => onPageChange(1)} disabled={currentPage === 1 || isLoading}>First</Button>
            <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading}>Prev</Button>
            <span className="px-3 text-sm text-muted-foreground">Page {currentPage}/{totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading}>Next</Button>
            <Button variant="outline" size="sm" onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages || isLoading}>Last</Button>
          </div>
        )}
      </div>
    </div>
  );
}
